export interface Rng {
  state: number;
  next(): number;
  intInRange(minInclusive: number, maxInclusive: number): number;
  pick<T>(arr: readonly T[]): T;
  shuffle<T>(arr: readonly T[]): T[];
  sample<T>(arr: readonly T[], n: number): T[];
}

export function createRng(seed: number): Rng {
  let state = seed >>> 0;
  if (state === 0) state = 0x9e3779b9;

  function next(): number {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  function intInRange(min: number, max: number): number {
    if (max < min) throw new Error('rng.intInRange: max < min');
    return min + Math.floor(next() * (max - min + 1));
  }

  function pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('rng.pick: empty array');
    return arr[intInRange(0, arr.length - 1)]!;
  }

  function shuffle<T>(arr: readonly T[]): T[] {
    const copy = arr.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = intInRange(0, i);
      const tmp = copy[i]!;
      copy[i] = copy[j]!;
      copy[j] = tmp;
    }
    return copy;
  }

  function sample<T>(arr: readonly T[], n: number): T[] {
    if (n > arr.length) {
      throw new Error(`rng.sample: requested ${n} from ${arr.length}`);
    }
    return shuffle(arr).slice(0, n);
  }

  return {
    get state() {
      return state;
    },
    set state(v: number) {
      state = v >>> 0;
    },
    next,
    intInRange,
    pick,
    shuffle,
    sample,
  } as Rng;
}

export function rngFromState(state: number): Rng {
  return createRng(state);
}
